import { Center, Flex, FlexProps, IconButton, IconButtonProps, Image, Spinner, useDisclosure } from '@chakra-ui/react';
import BrandKitImage from 'assets/images/brand_kit.png';
import { UPGRADE_REDIRECT_BIZ_LINK } from 'constants/links';
import { CLICK_ON_PAID_FEATURE, IMAGE_CREATED, UPGRADE } from 'constants/mixpanel';
import { useInfiniteScroll } from 'hooks/useInfiniteScrollHook';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { useChameleon } from 'libs/third-party/Chameleon';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { CommonGatedUpgradeModal } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal';
import { ModalUpgradeTypeEnum } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal.types';
import { WidgetDragDropWrapper } from 'modules/Editor/components/WidgetDragDrop';
import { PermissionBadge, UserTypes } from 'modules/User';
import { CSSProperties, ReactElement, memo, useCallback, useMemo, useRef, useState } from 'react';
import { VariableSizeGrid as Grid } from 'react-window';
import { WidgetType } from 'types/widget.types';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';
import { NewWidget } from 'widgets/Widget.types';

import { COLUMN_NUM, INDEX_OFFSET } from './InfiniteGrid.config';

export type CellMetaType = {
  previewHeight: number;
  tags: string;
  previewURL: string;
};

// Props required by react-window
// @see https://github.com/bvaughn/react-window/blob/master/src/createGridComponent.js
interface CellProps<T> {
  columnIndex: number;
  data: T;
  isScrolling?: boolean;
  rowIndex: number;
  style: CSSProperties;
}

type OnItemClickFnType = (itemData: unknown, index?: number, image?: HTMLImageElement | null) => Promise<unknown>;
type OnCreateWidgetData = (itemData: unknown, image?: HTMLImageElement | null) => NewWidget;

interface GridInterface {
  hits: CellMetaType[];
  onItemClick: OnItemClickFnType;
  cellButtonProps?: Partial<IconButtonProps>;
  onLoadMoreAnchor: (instance: Element | null) => void;
  createWidgetData?: OnCreateWidgetData;
  disableDragDrop?: boolean;
}

interface InfiniteGridProps extends Omit<FlexProps, 'ref'> {
  data: CellMetaType[];
  cellButtonProps?: Partial<IconButtonProps>;
  numberOfItems: number;
  onItemClick?: OnItemClickFnType;
  loadMore?: () => void;
  cell?: (_: CellProps<GridInterface>) => ReactElement | null;
  createWidgetData?: OnCreateWidgetData;
  disableDragDrop?: boolean;
}

export const InfiniteGrid = memo(
  ({
    cellButtonProps,
    data,
    numberOfItems,
    onItemClick,
    loadMore,
    cell: CellProp,
    createWidgetData,
    disableDragDrop,
  }: InfiniteGridProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [{ containerWidth, containerHeight }, setContainerSize] = useState({ containerWidth: 0, containerHeight: 0 });
    const [, onLoadAnchor] = useInfiniteScroll({ onLoadMore: loadMore, onLoadThreshold: 0.8 });

    // Update the infinite scroll box size when the window is resizing
    useResizeObserver({
      ref: containerRef,
      onResize: ({ width = 0, height = 0 }) => {
        setContainerSize({ containerWidth: width, containerHeight: height });
      },
    });

    const calculateRowHeight = (rowIndex: number) => {
      const imageIndex = rowIndex * COLUMN_NUM;

      // Get the max height of images on a row
      const maxHeightInARow = Math.max(data[imageIndex].previewHeight, data[imageIndex + 1]?.previewHeight || 0);
      return maxHeightInARow;
    };

    const gridData = useMemo(() => {
      const itemClickFn = onItemClick ?? (() => Promise.resolve());
      return {
        cellButtonProps,
        onItemClick: itemClickFn,
        onLoadMoreAnchor: onLoadAnchor,
        hits: data,
        createWidgetData,
        disableDragDrop,
      };
    }, [cellButtonProps, createWidgetData, data, disableDragDrop, onItemClick, onLoadAnchor]);

    const calculateColumnWidth = useCallback(() => {
      return containerWidth / 2;
    }, [containerWidth]);

    return (
      <Flex
        ref={containerRef}
        direction='column'
        h='full'
        w='full'
        maxW='full'
        maxH='full'
        overflow={'auto'}
        paddingTop={3}
        data-testid={'infinite-image-list'}
      >
        <Grid<GridInterface>
          key={containerWidth * containerHeight}
          itemData={gridData}
          columnCount={COLUMN_NUM}
          columnWidth={calculateColumnWidth}
          height={containerHeight}
          width={containerWidth}
          rowHeight={calculateRowHeight}
          rowCount={Math.ceil(numberOfItems / 2)}
          style={{ overflow: 'visible' }}
        >
          {CellProp ?? Cell}
        </Grid>
      </Flex>
    );
  },
);
InfiniteGrid.displayName = 'InfiniteGrid';

export const Cell = ({ columnIndex, rowIndex, style, data }: CellProps<GridInterface>) => {
  const index = rowIndex * COLUMN_NUM + columnIndex;
  const { cellButtonProps, hits, onItemClick, onLoadMoreAnchor, createWidgetData, disableDragDrop } = data;
  const imageRef = useRef<HTMLImageElement>(null);
  const hit = hits[index];

  const [isAddingImage, setIsAddingImage] = useState(false);

  if (!hit) return null;

  const onClick = () => {
    setIsAddingImage(true);
    onItemClick(hit, index, imageRef.current).finally(() => {
      setIsAddingImage(false);
    });
  };

  const onCreateWidgetData = () =>
    typeof createWidgetData === 'function' ? createWidgetData(hit, imageRef.current) : undefined;

  return (
    <Flex
      // Set load more anchor ref near the end of the list
      ref={index === hits.length - INDEX_OFFSET ? onLoadMoreAnchor : undefined}
      style={style}
      padding={2}
      justifyContent={'center'}
      alignItems={'center'}
    >
      <WidgetDragDropWrapper
        isDraggable={!disableDragDrop}
        widgetData={onCreateWidgetData()}
        type={WidgetType.Image}
        dragImage={
          <Flex w={style.width} h='auto'>
            <Center>
              <Image src={hit.previewURL} maxH='100%' />
            </Center>
          </Flex>
        }
        dragImageOffset={{ x: Number(style.width) / 2, y: Number(style.height) / 2 }}
        trackingEventData={{
          event_name: IMAGE_CREATED.eventName,
          properties: {
            from: IMAGE_CREATED.from.Brand,
          },
        }}
      >
        <IconButton
          aria-label={hit.tags}
          w='full'
          h='full'
          backgroundColor='gray.100'
          onClick={onClick}
          variant={'icon-btn-side-menu'}
          {...cellButtonProps}
        >
          <Image ref={imageRef} src={hit.previewURL} alt={hit.tags} maxH='100%' pointerEvents='none' />
        </IconButton>
      </WidgetDragDropWrapper>
      {isAddingImage && <LoadingOverlay />}
    </Flex>
  );
};

export const CellWithBadge = ({ columnIndex, rowIndex, style, data }: CellProps<GridInterface>) => {
  const index = rowIndex * COLUMN_NUM + columnIndex;
  const { cellButtonProps, hits, onLoadMoreAnchor } = data;
  const hit = hits[index];
  const { trackChameleon } = useChameleon();
  const { isOpen: isUpgradeModal, onOpen: onOpenUpgradeModal, onClose: onCloseUpgradeModal } = useDisclosure();

  if (!hit) return null;

  const onClick = () => {
    Mixpanel.track(UPGRADE.eventName, {
      Feature: UPGRADE.feature.BrandKitLogo,
      plan: UserTypes.Business,
    });
    trackChameleon(UPGRADE.eventName);

    setAnalyticsUpgradeFeatureProp(UPGRADE.feature.BrandKitLogo);

    window.open(UPGRADE_REDIRECT_BIZ_LINK, '_self');
  };

  const onClickUpgradeModal = () => {
    onOpenUpgradeModal();
    Mixpanel.track(CLICK_ON_PAID_FEATURE.eventName, {
      Feature: UPGRADE.feature.BrandKitLogo,
      plan: UserTypes.Business,
    });
  };

  return (
    <Flex
      // Set loadMore anchor ref near the end of the list
      ref={index === hits.length - INDEX_OFFSET ? onLoadMoreAnchor : undefined}
      style={style}
      padding={2}
      justifyContent={'center'}
      alignItems={'center'}
      role='group'
    >
      <CommonGatedUpgradeModal
        isOpen={isUpgradeModal}
        onClose={onCloseUpgradeModal}
        onClickUpgrade={onClick}
        upgradePlan={UserTypes.Business}
        modalImg={BrandKitImage}
        modalType={ModalUpgradeTypeEnum.BrandKit}
      />
      <IconButton
        aria-label={hit.tags}
        w='full'
        h='full'
        backgroundColor='gray.100'
        onClick={onClickUpgradeModal}
        variant={'icon-btn-side-menu'}
        {...cellButtonProps}
      >
        <Image src={hit.previewURL} alt={hit.tags} maxH='100%' pointerEvents='none' />
      </IconButton>
      <PermissionBadge permission='business' position='absolute' top={0} right={4} />
    </Flex>
  );
};

export const LoadingOverlay = () => {
  return (
    <Flex
      w='full'
      h='full'
      position={'absolute'}
      top={0}
      left={0}
      justifyContent={'center'}
      alignItems={'center'}
      cursor={'not-allowed'}
      bg='blackAlpha.300'
      data-testid={'image-adding-overlay'}
    >
      <Spinner color={'brand.500'} />
    </Flex>
  );
};
